<template>
  <div>
    <dashboard-header v-if="!hideHeader" data-tour="header" @tour="startTour" />

    <ai-credits-banner v-if="segmentationEnabled" />

    <no-data-modal
      :visible="!!notice && dismissedNotice !== notice.key"
      :refreshing="refreshing"
      :icon="notice ? notice.icon : undefined"
      :title="notice ? notice.title : undefined"
      :text="notice ? notice.text : undefined"
      :hint="notice ? notice.hint : undefined"
      :tone="notice ? notice.tone : undefined"
      :show-refresh="notice ? notice.refresh : true"
      :loading="!!notice && !!notice.loading"
      :progress="notice && notice.progress ? notice.progress : 0"
      :progress-text="notice && notice.progressText ? notice.progressText : ''"
      @close="dismissedNotice = notice.key"
      @refresh="$emit('refresh')"
    />

    <segments-overview
      :summary="summary"
      :insights="insights"
      :segment-stats="segmentStats"
      :segment-tags="segmentTags"
      :active-segment="activeSegment"
      :loading-summary="loadingSummary"
      :loading-insights="loadingInsights"
      :freshness="segmentFreshness"
      :refreshing="refreshing"
      @refresh="$emit('refresh')"
      @segment-click="handleSegmentClick"
    />

    <engagement-overview
      :audience-profiles="audienceProfiles"
      :cross-channel="crossChannel"
      :completeness="completeness"
      :loading-aggregate="loadingAggregate"
      :loading-completeness="loadingCompleteness"
      @export-pair="startExport($event)"
    />

    <listener-retargeting
      data-tour="listeners"
      :listeners="listeners.listeners || []"
      :total="listeners.totalMatches || 0"
      :loading="loadingListeners"
      :visible="showListeners"
      :has-brief="preFillBrief.text.length > 0"
      :is-search="!!localFilters.originator"
      @toggle-display="showListeners = !showListeners"
      @load-more="$emit('load-more')"
      @open="openListener"
      @bulk-enrich="showBulkEnrich = true"
      :retarget-disabled="retargetAlreadyAdded || addingToBucket"
      :retarget-loading="addingToBucket"
      :retarget-tip="retargetTip"
      @retarget="retargetCurrent"
      @export="startExport()"
      @ai-suggest="handleAiSuggest"
      @reset="resetFilters"
    />

    <campaign-recommender
      ref="recommender"
      data-tour="recommender"
      :recommendation-result="recommendationResult"
      :pre-fill="preFillBrief"
      :listener-channel-counts="channelCounts"
      :active-filters="localFilters"
      :credits-exhausted="creditsExhausted"
      @recommend="onRecommend"
      @export-audience="exportRecommended()"
      @export-by-channel="exportRecommended($event)"
      @cleared="onRecommenderCleared"
    />

    <dashboard-fab
      :icon="fabsOpen ? 'fa-chevron-down' : 'fa-th-large'"
      :label="fabsOpen ? 'Hide shortcuts' : 'Show shortcuts'"
      :bottom="32"
      type="info"
      :badge="fabsOpen ? 0 : shortcutAlerts"
      tour-key="fab-toggle"
      @open="toggleFabs"
    />

    <filter-fab
      v-show="fabsOpen"
      :bottom="104"
      :active-count="activeFilterCount"
      @open="openFilters"
    />

    <template v-if="!hideHeader">
      <dashboard-fab
        v-show="fabsOpen"
        icon="fa-search"
        label="Find listeners"
        tour-key="fab-find"
        :bottom="176"
        @open="showFind = true"
      />
      <dashboard-fab
        v-show="fabsOpen"
        icon="fa-users"
        label="Audiences"
        tour-key="fab-audiences"
        :bottom="248"
        @open="showAudiences = true"
      />

      <dashboard-fab
        v-show="fabsOpen"
        icon="fa-bullseye"
        label="Advertiser hub"
        tour-key="fab-advertiser"
        :bottom="320"
        @open="showAdvertiserHub = true"
      />

      <find-listeners-modal
        :visible="showFind"
        :total="listeners.totalMatches || 0"
        :loading-listeners="loadingListeners"
        @close="showFind = false"
        @search="findByTerm"
        @view="scrollToListeners"
        @apply-ai="applyAiSearch"
      />

      <dashboard-fab
        v-show="fabsOpen"
        icon="fa-paper-plane"
        label="Retarget bucket"
        tour-key="fab-bucket"
        :bottom="392"
        :badge="bucketItems"
        @open="openBucket"
      />

      <ai-usage-fab v-show="fabsOpen" :bottom="464" />

      <advertiser-hub-modal
        :visible="showAdvertiserHub"
        :filters="bulkEnrichFilters"
        :filter-labels="bulkEnrichFilterLabels"
        @close="showAdvertiserHub = false"
        @pick="handleHubPick"
        @open-filters="openFilters"
      />

      <audiences-modal
        :visible="showAudiences"
        @close="showAudiences = false"
        @view-members="viewAudience"
        @retarget="retargetAudiences"
      />

      <retarget-bucket-modal
        :visible="showBucket"
        :groups="bucket.groups"
        :total="bucket.total"
        :loading="bucketLoading"
        @close="showBucket = false"
        @changed="loadBucket"
        @export="startExport($event, true)"
      />

      <export-fields-modal
        :visible="!!pendingExport"
        :source-label="pendingExport ? pendingExport.label : ''"
        :bucket="!!(pendingExport && pendingExport.bucket)"
        :busy="exporting"
        :consent-total="exportCounts ? exportCounts.total : 0"
        :consent-without="exportCounts ? exportCounts.without_opt_in : 0"
        :consent-loading="exportCountsLoading"
        @close="pendingExport = null"
        @confirm="runExport"
      />

      <consent-check-modal
        :visible="!!consent"
        :purpose="consent ? consent.purpose : 'retarget'"
        :total="consent ? consent.total : 0"
        :without="consent ? consent.without : 0"
        @close="answerConsent(null)"
        @confirm="answerConsent($event)"
      />

      <bulk-enrich-modal
        :visible="showBulkEnrich"
        :segment-counts="segmentCounts"
        :filters="bulkEnrichFilters"
        :filter-labels="bulkEnrichFilterLabels"
        :filter-total="listeners.totalMatches || 0"
        @close="showBulkEnrich = false"
        @finished="refreshListeners"
      />
    </template>

    <segmentation-tour :visible="tourOpen" :steps="tourSteps" @step="onTourStep" @close="endTour" />

    <audience-filters
      v-if="filtersMounted"
      :visible="showFilters"
      :filters="localFilters"
      :options="filterOptions"
      :counts="scopedCounts"
      :segment-counts="segmentCounts"
      :missing-field-counts="completeness.missingFieldCounts || {}"
      @close="showFilters = false"
      @update="onFilterUpdate"
      @apply="applyFilters"
      @reset="resetFilters"
    />

    <listener-dialog
      v-if="selectedListener"
      :listener="selectedListener"
      :visible="showDialog"
      :enriching="enriching"
      :enrich-credits-exhausted="enrichCreditsExhausted"
      :bucket-groups="bucket.groups"
      :retarget-default-label="listenerRetargetTarget.label"
      :retargeting="addingToBucket"
      :match-reason="dialogReason"
      :audience-name="matchSourceName"
      @close="closeDialog"
      @view-profile="$emit('view-profile', $event)"
      @enrich="$emit('enrich', $event)"
      @find-similar="$emit('find-similar', $event)"
      @retarget="retargetListener"
      @apply-inferred="$emit('apply-inferred', $event)"
      @review-link="$emit('review-link', $event)"
      @replace-demographic="$emit('replace-demographic', $event)"
    />
  </div>
</template>

<script>
import DashboardHeader from '@/components/segmentation/partials/DashboardHeader';
import NoDataModal from '@/components/segmentation/common/NoDataModal';
import AiCreditsBanner from '@/components/segmentation/common/AiCreditsBanner';
import SectionSkeleton from '@/components/segmentation/common/SectionSkeleton';
import { TOUR_STEPS } from '@/components/segmentation/utils/tourSteps';

import moment from 'moment';
import { getSegmentLabel } from '@/components/segmentation/utils/segmentation';
import { toCsv } from '@/components/segmentation/utils/exportFields';
import retargetBucketQuery from '@/graphql/segmentation/retarget/retargetBucketQuery.gql';
import addToRetargetBucketMutation from '@/graphql/segmentation/retarget/addToRetargetBucketMutation.gql';
import exportRowsQuery from '@/graphql/segmentation/retarget/exportRowsQuery.gql';
import retargetConsentCheckQuery from '@/graphql/segmentation/retarget/retargetConsentCheckQuery.gql';
import exportConsentCheckQuery from '@/graphql/segmentation/retarget/exportConsentCheckQuery.gql';
import {
  DEFAULT_FILTERS,
  buildFilterOptions,
  buildActivePills,
  buildFilterParams,
  filtersFromAiSearch,
  countActiveFilters,
  deriveScopedCounts,
  buildFilterSummary,
} from '@/components/segmentation/utils/filterHelpers';

/**
 * The dashboard's components, loaded on demand and not with the first page.
 *
 * Note: The loading notice, header and credits banner are small and are
 * needed at once, so they stay in the main file. Everything else is fetched
 * while the loading notice is showing (see loadInterface), in two files: the
 * views and the dialogs (see lazy/). Opening a dialog later never waits for
 * a download.
 */
const loadViews = () => import('@/components/segmentation/lazy/views');
const loadDialogs = () => import('@/components/segmentation/lazy/dialogs');
const from = (load, name) => () => load().then(bundle => bundle.default[name]);
const LOADERS = {
  SegmentsOverview: from(loadViews, 'SegmentsOverview'),
  EngagementOverview: from(loadViews, 'EngagementOverview'),
  ListenerRetargeting: from(loadViews, 'ListenerRetargeting'),
  CampaignRecommender: from(loadViews, 'CampaignRecommender'),
  DashboardFab: from(loadViews, 'DashboardFab'),
  AiUsageFab: from(loadViews, 'AiUsageFab'),
  FilterFab: from(loadViews, 'FilterFab'),
  ListenerDialog: from(loadDialogs, 'ListenerDialog'),
  FindListenersModal: from(loadDialogs, 'FindListenersModal'),
  AudiencesModal: from(loadDialogs, 'AudiencesModal'),
  BulkEnrichModal: from(loadDialogs, 'BulkEnrichModal'),
  RetargetBucketModal: from(loadDialogs, 'RetargetBucketModal'),
  ExportFieldsModal: from(loadDialogs, 'ExportFieldsModal'),
  ConsentCheckModal: from(loadDialogs, 'ConsentCheckModal'),
  SegmentationTour: from(loadDialogs, 'SegmentationTour'),
  AudienceFilters: from(loadDialogs, 'AudienceFilters'),
  AdvertiserHubModal: from(loadDialogs, 'AdvertiserHubModal'),
};

/**
 * The height of each main section (in pixels), used for the placeholder
 * shown while its code downloads.
 */
const SECTION_HEIGHTS = {
  SegmentsOverview: 460,
  EngagementOverview: 520,
  ListenerRetargeting: 300,
  CampaignRecommender: 220,
};

/**
 * A lazy component. A main section gets a shimmering placeholder card while
 * it downloads; dialogs and buttons need none.
 */
const lazyComponent = name => {
  const height = SECTION_HEIGHTS[name];
  if (!height) {
    return LOADERS[name];
  }
  const loading = {
    functional: true,
    render: h => h(SectionSkeleton, { props: { height } }),
  };
  return () => ({ component: LOADERS[name](), loading, delay: 0 });
};

export default {
  name: 'SegmentationDashboard',
  components: {
    DashboardHeader,
    NoDataModal,
    AiCreditsBanner,
    ...Object.assign({}, ...Object.keys(LOADERS).map(name => ({ [name]: lazyComponent(name) }))),
  },
  props: {
    hideHeader: { type: Boolean, default: false },
    summary: {
      type: Object,
      default: () => ({ totalListeners: 0, highEngagementCount: 0, segmentCounts: {} }),
    },
    insights: { type: Object, default: () => ({ segments: [] }) },
    listeners: { type: Object, default: () => ({ listeners: [], totalMatches: 0 }) },
    segmentStats: { type: Array, default: () => [] },
    segmentTags: { type: Object, default: () => ({}) },
    aggregate: {
      type: Object,
      default: () => ({
        total: 0,
        channelCounts: {},
        genderCounts: {},
        ageGroupCounts: {},
        languageCounts: {},
        locationCounts: {},
        segmentCounts: {},
        momentumCounts: {},
        campaignTypeCounts: {},
        ethnicityCounts: {},
        tagCounts: {},
      }),
    },
    crossChannel: {
      type: Object,
      default: () => ({ total: 0, channels: [], uniques: {}, matrix: {}, percentages: {} }),
    },
    baseAggregate: {
      type: Object,
      default: () => ({
        total: 0,
        channelCounts: {},
        genderCounts: {},
        ageGroupCounts: {},
        languageCounts: {},
        locationCounts: {},
        ethnicityCounts: {},
        tagCounts: {},
      }),
    },
    completeness: {
      type: Object,
      default: () => ({
        total: 0,
        completeCount: 0,
        incompleteCount: 0,
        missingFieldCounts: {},
      }),
    },
    selectedListener: { type: Object, default: null },
    recommendationResult: { type: Object, default: null },
    recommendError: { type: Boolean, default: false },
    creditsExhausted: { type: Boolean, default: false },
    segmentFreshness: { type: Object, default: () => ({ data_updated_at: null, segments: {} }) },
    refreshing: { type: Boolean, default: false },
    enriching: { type: Boolean, default: false },
    enrichCreditsExhausted: { type: Boolean, default: false },
    loadingSummary: { type: Boolean, default: false },
    /**
     * How many of the first-load queries have finished, out of how many,
     * and the messages of the ones that failed. Without them the dashboard
     * cannot tell "still loading" from "no data".
     */
    loadTotal: { type: Number, default: 0 },
    loadDone: { type: Number, default: 0 },
    loadErrors: { type: Array, default: () => [] },
    segmentationEnabled: { type: Boolean, default: true },
    loadingInsights: { type: Boolean, default: false },
    loadingListeners: { type: Boolean, default: false },
    loadingAggregate: { type: Boolean, default: false },
    loadingCompleteness: { type: Boolean, default: false },
  },

  data: () => ({
    interfaceReady: false,
    activeSegment: null,
    showListeners: false,
    showDialog: false,
    openedReason: null,
    showFilters: false,
    filtersMounted: false,
    showFind: false,
    showAudiences: false,
    showBulkEnrich: false,
    /**
     * The key of the notice the station closed. A different notice (for
     * example an error after "no data") still shows.
     */
    dismissedNotice: '',
    showBucket: false,
    tourOpen: false,
    tourSteps: TOUR_STEPS,
    fabsOpen: false,
    showAdvertiserHub: false,
    fabsBeforeTour: null,
    bucket: { groups: [], total: 0 },
    bucketLoading: false,
    addingToBucket: false,
    lastBucketKey: '',
    pendingExport: null,
    exporting: false,
    consent: null,
    exportCounts: null,
    exportCountsLoading: false,
    preFillBrief: { text: '', ts: 0 },
    localFilters: { ...DEFAULT_FILTERS },
    cachedFilterOptions: {
      genders: [],
      ageGroups: [],
      languages: [],
      locations: [],
      ethnicities: [],
      tags: [],
      channels: [],
      lsmTiers: [],
    },
  }),

  computed: {
    dialogReason() {
      const opened = this.openedReason;
      if (!opened || !this.selectedListener) {
        return null;
      }
      return opened.originator === this.selectedListener.originator ? opened.reason : null;
    },
    segmentCounts() {
      return this.summary.segmentCounts || {};
    },

    channelCounts() {
      return (this.aggregate && this.aggregate.channelCounts) || {};
    },

    scopedCounts() {
      return deriveScopedCounts(this.aggregate || {});
    },

    audienceProfiles() {
      const agg = this.aggregate;
      if (!agg || !agg.total) {
        return null;
      }
      const scoped = this.scopedCounts;
      return {
        gender: scoped.genderCounts,
        language: scoped.languageCounts,
        ageGroup: scoped.ageGroupCounts,
        location: scoped.locationCounts,
        ethnicity: scoped.ethnicityCounts,
      };
    },

    filterOptions() {
      return this.cachedFilterOptions;
    },

    retargetAlreadyAdded() {
      const { key } = this.currentGroup();
      return this.bucket.groups.some(g => g.group_key === key && g.full);
    },

    retargetTip() {
      return this.retargetAlreadyAdded
        ? 'This list is already in the retargeting bucket. Change the filters to add a different one.'
        : 'Set everyone in the current list aside in the retargeting bucket';
    },

    listenerRetargetTarget() {
      const last = this.bucket.groups.find(g => g.group_key === this.lastBucketKey);
      if (last) {
        return { key: last.group_key, label: last.label, kind: last.kind };
      }
      return this.contextGroup(this.selectedListener);
    },

    shortcutAlerts() {
      return this.activeFilterCount + (this.bucket.groups || []).length;
    },

    bucketItems() {
      return (this.bucket.groups || []).length;
    },

    activeFilterCount() {
      return countActiveFilters(this.localFilters);
    },

    /**
     * What the pop-up should say, or null when there is nothing to say.
     *
     * Note: It shows a loading pop-up until every first-load query has
     * finished, then closes it, or lists what failed. A station that does
     * have data never sees "no data" flash while it loads.
     */
    notice() {
      if (!this.segmentationEnabled) {
        return {
          key: 'unavailable',
          tone: 'warning',
          icon: 'fa-ban',
          title: "Segmentation isn't available",
          text: "Segmentation isn't turned on for this station. Contact your account manager to have it enabled.",
          hint: '',
          refresh: false,
        };
      }
      const total = this.loadTotal + 1;
      const done = this.loadDone + (this.interfaceReady ? 1 : 0);
      if (this.loadTotal > 0 && done < total) {
        const percent = Math.round((done / total) * 100);
        return {
          key: 'loading',
          tone: 'info',
          icon: 'fa-bar-chart',
          title: 'Loading segmentation data',
          text: 'Getting your segments, insights and listeners ready…',
          progressText: `${done} of ${total} loaded · ${percent}%`,
          hint: '',
          refresh: false,
          loading: true,
          progress: percent,
        };
      }
      if (this.loadErrors.length) {
        return {
          key: `error:${this.loadErrors.join('|')}`,
          tone: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Could not load segmentation data',
          text: this.loadErrors.join('\n'),
          hint: 'The segmentation service may be down or restarting. Try again in a moment.',
          refresh: true,
        };
      }
      if (this.loadingSummary) {
        return null;
      }
      if (!(this.summary && this.summary.totalListeners)) {
        return {
          key: 'empty',
          tone: 'info',
          icon: 'fa-database',
          title: 'No listener data yet',
          text: "Segmentation is turned on for this station, but there is no listener data to show. Segments, insights and charts appear here once the station's data has been loaded.",
          hint: 'If the data was loaded recently, refresh to check again.',
          refresh: true,
        };
      }
      return null;
    },

    /**
     * What the listener's "why they match" panel names: the saved audience
     * being viewed, or else the interests being filtered on.
     */
    matchSourceName() {
      const f = this.localFilters;
      if (f.audienceName) {
        return f.audienceName;
      }
      const interests = [...(f.interests || []), ...(f.interestsAny || [])];
      return interests.length ? `interest: ${interests.join(', ')}` : '';
    },

    /**
     * The filters AI enrichment can be limited to: the ones the listing is
     * using, sent the same way the listing sends them. Empty when nothing
     * is filtered.
     */
    bulkEnrichFilters() {
      return this.activeFilterCount > 0 ? buildFilterParams(this.localFilters) : {};
    },

    /**
     * One short text per active filter, for the enrich dialog to show.
     */
    bulkEnrichFilterLabels() {
      if (!this.activeFilterCount) {
        return [];
      }
      const pills = buildActivePills(this.localFilters, this.filterOptions.timeslots);
      return Object.values(pills);
    },
  },

  watch: {
    pendingExport(job) {
      this.loadExportCounts(job);
    },
    baseAggregate: {
      immediate: true,
      handler(agg) {
        if (!agg || !agg.total) {
          return;
        }
        const hasOptions =
          this.cachedFilterOptions.genders.length > 0 ||
          this.cachedFilterOptions.channels.length > 0;
        if (hasOptions) {
          return;
        }
        this.cachedFilterOptions = buildFilterOptions(agg);
      },
    },
    selectedListener(val) {
      this.showDialog = !!val;
    },
    localFilters: {
      deep: true,
      handler() {
        this.preFillBrief = { text: '', ts: Date.now() };
      },
    },
    recommendError(val) {
      if (val && this.$refs.recommender) {
        this.$refs.recommender.onError();
      }
    },
  },

  created() {
    this.loadInterface();
    if (!this.hideHeader) {
      this.loadBucket();
    }
  },

  methods: {
    /**
     * Downloads the dashboard's lazy components while the data loads, and
     * counts them as one more item in the loading notice.
     *
     * Note: A download that fails still marks the interface ready, so the
     * notice never stays up for good. Vue reports the missing component
     * when it tries to draw it.
     */
    loadInterface() {
      const ready = () => {
        this.interfaceReady = true;
      };
      Promise.all(Object.values(LOADERS).map(load => load())).then(ready, ready);
    },

    openFilters() {
      if (this.filtersMounted) {
        this.showFilters = true;
        return;
      }
      this.filtersMounted = true;
      this.$nextTick(() => {
        this.showFilters = true;
      });
    },

    startTour() {
      this.fabsBeforeTour = this.fabsOpen;
      this.tourOpen = true;
    },
    onTourStep(step) {
      if (step && step.fab) {
        this.fabsOpen = true;
      }
    },
    endTour() {
      this.tourOpen = false;
      if (this.fabsBeforeTour !== null) {
        this.fabsOpen = this.fabsBeforeTour;
        this.fabsBeforeTour = null;
      }
    },

    toggleFabs() {
      this.fabsOpen = !this.fabsOpen;
    },

    /**
     * Loads the retarget bucket, for the count on its button.
     *
     * Note: If it cannot be loaded the bucket button simply shows no count.
     */
    async loadBucket() {
      this.bucketLoading = true;
      try {
        const { data } = await this.$apollo.query({
          client: 'apiv2',
          query: retargetBucketQuery,
          fetchPolicy: 'network-only',
        });
        this.bucket = data.retargetBucket || { groups: [], total: 0 };
      } catch (e) {
        /* Ignored on purpose: see the Note above the function. */
      } finally {
        this.bucketLoading = false;
      }
    },

    openBucket() {
      this.showBucket = true;
      this.loadBucket();
    },

    /**
     * Sets listeners aside in the bucket. `source` is one of
     * { originators }, { audienceId } or { params } (a filter set).
     */
    async addToBucket(group, source) {
      if (this.addingToBucket) {
        return false;
      }
      this.addingToBucket = true;

      const include = await this.consentChoice(
        'retarget',
        retargetConsentCheckQuery,
        source,
        'retargetConsentCheck',
      );
      if (include === null) {
        this.addingToBucket = false;
        return false;
      }

      const working = this.$notify({
        title: 'Retargeting bucket',
        message: `Processing… adding listeners to "${group.label}". This can take a few seconds for large lists.`,
        iconClass: 'fa fa-spinner fa-spin',
        duration: 0,
        showClose: false,
      });
      try {
        const { data } = await this.$apollo.mutate({
          client: 'apiv2',
          mutation: addToRetargetBucketMutation,
          variables: {
            groupKey: group.key,
            groupLabel: group.label,
            groupKind: group.kind,
            includeUnconsented: include,
            ...source,
          },
        });
        const result = data.addToRetargetBucket || {};
        const added = result.added || 0;
        const already = result.already_present || 0;
        working.close();

        if (added === 0 && already > 0) {
          this.$notify({
            type: 'warning',
            title: 'Already in the bucket',
            message:
              already === 1
                ? `This listener is already in "${group.label}". Choose a different group, or create a new one.`
                : `Everyone in this list is already in "${group.label}". Choose a different group, or create a new one.`,
            duration: 6000,
          });
          return false;
        }

        this.$notify({
          type: result.truncated ? 'warning' : 'success',
          title: 'Retargeting bucket',
          message: `${
            `${added.toLocaleString()} ${added === 1 ? 'listener' : 'listeners'} added to ` +
            `"${group.label}".`
          }${
            already
              ? ` ${already.toLocaleString()} ${already === 1 ? 'was' : 'were'} already there.`
              : ''
          }${
            result.excluded
              ? ` ${result.excluded.toLocaleString()} left out because they haven't opted in.`
              : ''
          }${result.truncated ? ' The list was larger than the group limit, so it was cut.' : ''}`,
        });
        await this.loadBucket();
        return true;
      } catch (e) {
        working.close();
        this.$notify({ type: 'error', message: 'Could not add to the retargeting bucket.' });
        return false;
      } finally {
        this.addingToBucket = false;
      }
    },

    /**
     * The retarget group for the filters currently applied.
     *
     * Note: Same filters -> same group, so re-adding merges instead of
     * duplicating.
     */
    currentGroup() {
      const f = this.localFilters;
      if (f.audienceId) {
        return {
          key: `audience:${f.audienceId}`,
          label: f.audienceName || 'Audience',
          kind: 'audience',
        };
      }
      if (f.segments.length === 1 && countActiveFilters(f) === 1) {
        return {
          key: `segment:${f.segments[0]}`,
          label: `${getSegmentLabel(f.segments[0])} segment`,
          kind: 'segment',
        };
      }
      const summary = buildFilterSummary(f);
      if (!summary) {
        return { key: 'filters:all', label: 'All listeners', kind: 'filters' };
      }
      return {
        key: `filters:${JSON.stringify(buildFilterParams(f))}`.slice(0, 190),
        label: summary.charAt(0).toUpperCase() + summary.slice(1),
        kind: 'filters',
      };
    },

    retargetCurrent() {
      const group = this.currentGroup();
      const source = this.localFilters.audienceId
        ? { audienceId: this.localFilters.audienceId }
        : { params: buildFilterParams(this.localFilters) };
      this.addToBucket(group, source);
    },

    contextGroup(listener) {
      if (countActiveFilters(this.localFilters) > 0) {
        return this.currentGroup();
      }
      if (listener && listener.currentSegment) {
        return {
          key: `segment:${listener.currentSegment}`,
          label: `${getSegmentLabel(listener.currentSegment)} segment`,
          kind: 'segment',
        };
      }
      return { key: 'listener:individual', label: 'Individual listeners', kind: 'listener' };
    },

    /**
     * target: {} (default group), { groupKey } (an existing bucket group) or
     * { newName } (a custom group). The group used is remembered, so the next
     * listener goes into the same one.
     */
    async retargetListener({ listener, target = {} }) {
      let group;
      if (target.groupKey) {
        const existing = this.bucket.groups.find(g => g.group_key === target.groupKey);
        group = existing
          ? { key: existing.group_key, label: existing.label, kind: existing.kind }
          : this.contextGroup(listener);
      } else if (target.newName) {
        const slug = target.newName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
        group = { key: `custom:${slug || 'group'}`, label: target.newName, kind: 'custom' };
      } else {
        group = this.listenerRetargetTarget;
      }

      const added = await this.addToBucket(group, { originators: [listener.originator] });
      if (added) {
        this.lastBucketKey = group.key;
      }
    },

    /**
     * One audience or several: each goes in as its own group, in turn.
     */
    async retargetAudiences(audiences) {
      const list = Array.isArray(audiences) ? audiences : [audiences];
      await list.reduce(
        (previous, audience) =>
          previous.then(() =>
            this.addToBucket(
              { key: `audience:${audience.audience_id}`, label: audience.name, kind: 'audience' },
              { audienceId: audience.audience_id },
            ),
          ),
        Promise.resolve(),
      );
    },

    viewAudience(audience) {
      this.showAudiences = false;
      this.localFilters = {
        ...DEFAULT_FILTERS,
        audienceId: audience.audience_id,
        audienceName: audience.name,
      };
      this.activeSegment = null;
      this.applyFilters();
    },

    startExport(options = {}, fromBucket = false) {
      const channel =
        options.channelType !== undefined && options.channelType !== null
          ? options.channelType
          : null;
      this.pendingExport = {
        bucket: fromBucket,
        groupKeys: options.groupKeys || [],
        channelType: channel,
        channelPair: options.channelPair || null,
        tags: options.tags || null,
        label: this.exportLabel(options, fromBucket),
      };
    },

    exportLabel(options, fromBucket) {
      const base = fromBucket
        ? options.label || 'Retarget bucket'
        : this.localFilters.audienceName || 'Current listener list';
      return options.label && !fromBucket ? `${base} · ${options.label}` : base;
    },

    /**
     * What an export covers: the bucket, an audience or the current
     * filters, narrowed by channel.
     */
    exportSource(job) {
      const source = {};
      if (job.bucket) {
        source.useBucket = true;
        source.groupKeys = job.groupKeys;
      } else if (this.localFilters.audienceId) {
        source.audienceId = this.localFilters.audienceId;
      } else {
        source.params = { ...buildFilterParams(this.localFilters) };
        if (job.tags) {
          source.params.tags = job.tags;
        }
      }
      if (job.channelType !== null) {
        source.channelType = job.channelType;
      }
      if (job.channelPair) {
        source.channelPair = job.channelPair;
      }
      return source;
    },

    /**
     * Asks how many listeners an add to the bucket covers who have not
     * opted in, and lets the station decide.
     *
     * Resolves null if they cancel, otherwise whether to include them.
     *
     * Note: If the check itself fails, listeners who have not opted in stay
     * out, since the server leaves them out unless told otherwise.
     */
    async consentChoice(purpose, query, variables, field) {
      let counts;
      try {
        const { data } = await this.$apollo.query({
          client: 'apiv2',
          query,
          variables,
          fetchPolicy: 'network-only',
        });
        counts = data[field];
      } catch (e) {
        return false;
      }
      if (!counts || !counts.without_opt_in) {
        return false;
      }
      return new Promise(resolve => {
        this.consent = {
          purpose,
          total: counts.total,
          without: counts.without_opt_in,
          resolve,
        };
      });
    },

    /**
     * Counts who has not opted in for the export being set up, so the
     * dialog can show it up front.
     *
     * Note: If the count cannot be loaded the dialog shows no consent
     * section, and the server still leaves those listeners out.
     */
    async loadExportCounts(job) {
      this.exportCounts = null;
      if (!job) {
        return;
      }
      this.exportCountsLoading = true;
      try {
        const { data } = await this.$apollo.query({
          client: 'apiv2',
          query: exportConsentCheckQuery,
          variables: this.exportSource(job),
          fetchPolicy: 'network-only',
        });
        if (this.pendingExport === job) {
          this.exportCounts = data.exportConsentCheck;
        }
      } catch (e) {
        /* Ignored on purpose: see the Note above the function. */
      } finally {
        if (this.pendingExport === job) {
          this.exportCountsLoading = false;
        }
      }
    },

    answerConsent(include) {
      const { resolve } = this.consent || {};
      this.consent = null;
      if (resolve) {
        resolve(include);
      }
    },

    async runExport({ fields, nonNull, includeUnconsented }) {
      const job = this.pendingExport;
      if (!job) {
        return;
      }
      const source = this.exportSource(job);
      this.exporting = true;
      const working = this.$notify({
        title: 'Export',
        message: 'Processing… building your CSV. This can take a moment for large lists.',
        iconClass: 'fa fa-spinner fa-spin',
        duration: 0,
        showClose: false,
      });
      try {
        const variables = {
          fields,
          nonNull,
          includeUnconsented: includeUnconsented === true,
          ...source,
        };

        const { data } = await this.$apollo.query({
          client: 'apiv2',
          query: exportRowsQuery,
          variables,
          fetchPolicy: 'network-only',
        });
        const result = data.exportRows;
        working.close();

        if (!result || !result.returned) {
          this.$notify({ type: 'warning', message: 'No listeners to export.' });
          return;
        }

        const blob = new Blob([toCsv(result)], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${job.label
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')}-${moment().format('YYYYMMDD-HHmm')}.csv`;
        a.click();
        URL.revokeObjectURL(url);

        this.$notify({
          type: 'success',
          message: `Exported ${result.returned.toLocaleString()} of ${result.total.toLocaleString()} listeners.${
            result.excluded
              ? ` ${result.excluded.toLocaleString()} left out because they haven't opted in.`
              : ''
          }${result.truncated ? ' The list was larger than the export limit, so it was cut.' : ''}`,
        });
        this.pendingExport = null;
      } catch (e) {
        working.close();
        this.$notify({ type: 'error', message: 'Export failed. Please try again.' });
      } finally {
        this.exporting = false;
      }
    },

    onFilterUpdate({ field, value }) {
      this.$set(this.localFilters, field, value);
    },

    applyAiSearch(aiFilters) {
      this.localFilters = filtersFromAiSearch(aiFilters);
      this.activeSegment =
        this.localFilters.segments.length === 1 ? this.localFilters.segments[0] : null;
      this.applyFilters();
    },
    onRecommend({ brief, filters }) {
      this.$emit('recommend', { brief, filters: buildFilterParams(filters) });
    },
    onRecommenderCleared() {
      this.preFillBrief = { text: '', ts: 0 };
      this.$emit('recommend-cleared');
    },
    /**
     * A number in the Buying intent card: lists the listeners at that
     * category and tier (or higher), keeping the other filters.
     */
    /**
     * Exporting from the recommender is how a recommendation counts as used.
     * The station is told which one, and the export carries on as before.
     */
    exportRecommended(channel) {
      const result = this.recommendationResult || {};
      const id = result.recommendation_id || result.recommendationId;
      if (id) {
        this.$emit('recommendation-used', {
          id,
          action: channel ? 'export_by_channel' : 'export_audience',
        });
      }
      this.startExport(channel);
    },

    handleHubPick(pick) {
      this.showAdvertiserHub = false;
      this.handleIntentPick(pick);
    },

    handleIntentPick({ category, tier }) {
      this.localFilters.intentCategories = [category];
      this.localFilters.intentTier = tier;
      this.preFillBrief = { text: '', ts: Date.now() };
      this.applyFilters();
    },

    handleSegmentClick(segment) {
      if ((this.segmentCounts[segment] || 0) === 0) {
        return;
      }

      this.activeSegment = this.activeSegment === segment ? null : segment;
      this.localFilters.segments = this.activeSegment ? [this.activeSegment] : [];
      this.preFillBrief = { text: '', ts: Date.now() };

      this.applyFilters({ scrollTo: '.seg-insights-row' });
    },

    /**
     * Plain search from the Find listeners window: runs here, and the
     * window reports the count; "View" then scrolls to the results.
     */
    findByTerm({ originator }) {
      this.localFilters = { ...DEFAULT_FILTERS, originator };
      this.activeSegment = null;
      this.applyFilters({ scrollTo: null });
    },

    scrollToListeners() {
      this.$nextTick(() => {
        const el = document.querySelector('.listener-retargeting-ibox');
        if (el) {
          window.scrollTo({
            top: el.getBoundingClientRect().top + window.scrollY - 20,
            behavior: 'smooth',
          });
        }
      });
    },

    /**
     * After a bulk enrich: reload what is on screen with the same filters.
     */
    refreshListeners() {
      this.$emit('filter', buildFilterParams(this.localFilters));
    },

    applyFilters({ scrollTo = '.listener-retargeting-ibox' } = {}) {
      const params = buildFilterParams(this.localFilters);

      this.preFillBrief = { text: '', ts: Date.now() };
      this.showListeners = true;
      this.$emit('filter', params);

      this.$nextTick(() => {
        const el = scrollTo && document.querySelector(scrollTo);

        if (el) {
          const offset = 20;
          const top = el.getBoundingClientRect().top + window.scrollY - offset;

          window.scrollTo({
            top,
            behavior: 'smooth',
          });
        }
      });
    },

    resetFilters() {
      this.localFilters = { ...DEFAULT_FILTERS };
      this.activeSegment = null;
      this.showListeners = false;
      this.preFillBrief = { text: '', ts: Date.now() };
      this.$emit('filter', {});
    },

    handleAiSuggest({ segment }) {
      const f = this.localFilters;
      const seg = f.segments[0] || segment;
      const summary = buildFilterSummary(f);
      const text = summary
        ? `Campaign targeting ${summary}`
        : `Campaign targeting ${getSegmentLabel(seg || 'general')} audience`;
      this.preFillBrief = { text, ts: Date.now() };
      this.$notify({
        type: 'info',
        title: 'AI Recommender',
        message: 'Brief pre-filled from your filters. Scroll down and click Generate.',
      });
      this.$nextTick(() => {
        const el = document.querySelector('.campaign-recommender-ibox');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },

    /**
     * Remembered so the Interactions tab can show only what matched the
     * audience being viewed.
     */
    openListener(listener) {
      this.openedReason = listener.matchReason
        ? { originator: listener.originator, reason: listener.matchReason }
        : null;
      this.$emit('open-listener', listener);
    },

    closeDialog() {
      this.showDialog = false;
      this.openedReason = null;
      this.$emit('close-listener');
    },
  },
};
</script>



// WEBPACK FOOTER //
// Dashboard.vue?67d86779